import { useState } from "react";
import { api, downloadBlob, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, FileDoc, FileZip, MapTrifold, Download, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ReportExportPanel({ project, catalog, mode }) {
  const id = project.id;
  const [rt, setRt] = useState("Detailed Mineral-Targeting Report");
  const [busy, setBusy] = useState(null);
  const [qbusy, setQbusy] = useState(false);
  const [qa, setQa] = useState(null);
  const hasTerrain = !!project.terrain;
  const [t3Elev, setT3Elev] = useState(Math.round(project.branding?.terrain3d_view?.elev ?? 42));
  const [t3Azim, setT3Azim] = useState(Math.round(project.branding?.terrain3d_view?.azim ?? -58));
  const [t3Url, setT3Url] = useState(null);
  const [t3Busy, setT3Busy] = useState(false);

  const dl = async (path, filename, key) => {
    setBusy(key);
    try { await downloadBlob(path, filename); toast.success("Download started"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const runQA = async () => {
    setQbusy(true);
    try {
      const res = await api.get(`/projects/${id}/report/qa?report_type=${encodeURIComponent(rt)}`);
      const data = res.data || {};
      const warnings = Array.isArray(data.warnings) ? data.warnings : [];
      setQa({ ok: !!data.ok, warnings });
      if (data.ok) toast.success("Report QA passed");
      else toast.warning(`Report QA found ${warnings.length} issue(s)`);
    } catch (e) {
      toast.error(apiError(e));
      setQa({ ok: false, warnings: [apiError(e)] });
    } finally { setQbusy(false); }
  };

  // QA Auto-Gate: run the page-render check right before every download, warn on issues, download anyway
  const dlReport = async (fmt) => {
    setBusy(fmt);
    try {
      let data = null;
      try { const r = await api.get(`/projects/${id}/report/qa?report_type=${encodeURIComponent(rt)}`); data = r.data; setQa({ ok: !!data.ok, warnings: data.warnings || [] }); } catch (_) { /* QA optional */ }
      if (data && !data.ok) toast.warning(`Report QA flagged ${(data.warnings || []).length} issue(s) - downloading anyway`);
      else if (data && data.ok) toast.success("Report QA passed - downloading");
      await downloadBlob(`/projects/${id}/report/${fmt}?report_type=${encodeURIComponent(rt)}`, `SYMIN_${project.name}.${fmt}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const previewT3 = async () => {
    setT3Busy(true);
    try {
      const res = await api.get(`/projects/${id}/terrain3d?elev=${t3Elev}&azim=${t3Azim}`, { responseType: "blob" });
      setT3Url((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(res.data); });
    } catch (e) { toast.error(apiError(e)); } finally { setT3Busy(false); }
  };
  const saveT3 = async () => {
    try {
      await api.post(`/projects/${id}/report-config`, { terrain3d_elev: Number(t3Elev), terrain3d_azim: Number(t3Azim) });
      toast.success("3D view saved - the report figure will use this camera angle");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (mode === "report") {
    return (
      <div className="space-y-4">
        <div>
          <p className="overline mb-2">Report Type</p>
          <Select value={rt} onValueChange={setRt}>
            <SelectTrigger className="h-9" data-testid="report-type"><SelectValue /></SelectTrigger>
            <SelectContent>{(catalog?.report_types || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <Button onClick={() => dlReport("pdf")} disabled={busy === "pdf"} data-testid="dl-pdf">
            <FilePdf size={16} className="mr-2" />{busy === "pdf" ? "Checking & preparing…" : "Download PDF Report"}
          </Button>
          <Button variant="outline" onClick={() => dlReport("docx")} disabled={busy === "docx"} data-testid="dl-docx">
            <FileDoc size={16} className="mr-2" />{busy === "docx" ? "Checking & preparing…" : "Download DOCX Report"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Reports include project details, data sources, CRS validation, processing methodology, ranked targets, AI interpretations, limitations and the scientific disclaimer.
        </p>
        <div className="border-t border-border pt-3">
          <p className="overline mb-2">Report QA Gate</p>
          <Button variant="outline" className="w-full" onClick={runQA} disabled={qbusy} data-testid="run-report-qa">
            <ShieldCheck size={16} className="mr-2" />{qbusy ? "Running checks…" : "Run Report QA"}
          </Button>
          {qa && (
            <div className={`mt-2 text-xs rounded-md border p-2 ${qa.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`} data-testid="report-qa-result">
              {qa.ok ? "All checks passed - no blank pages, content overflow or low-resolution figures detected."
                     : <ul className="list-disc pl-4 space-y-0.5">{qa.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">The QA gate now runs automatically right before every PDF/Word download; you can also run it on demand here.</p>
        </div>
        {hasTerrain && (
          <div className="border-t border-border pt-3" data-testid="t3-export">
            <p className="overline mb-2">3D Terrain View (report figure)</p>
            <div className="space-y-2">
              <label className="text-[11px] text-muted-foreground flex items-center justify-between">Elevation angle<span className="font-mono text-foreground">{t3Elev}°</span></label>
              <input type="range" min="5" max="89" value={t3Elev} onChange={(e) => setT3Elev(Number(e.target.value))} className="w-full" data-testid="t3-elev" />
              <label className="text-[11px] text-muted-foreground flex items-center justify-between">Azimuth (rotation)<span className="font-mono text-foreground">{t3Azim}°</span></label>
              <input type="range" min="-180" max="180" value={t3Azim} onChange={(e) => setT3Azim(Number(e.target.value))} className="w-full" data-testid="t3-azim" />
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" className="flex-1" onClick={previewT3} disabled={t3Busy} data-testid="t3-preview">{t3Busy ? "Rendering…" : "Preview"}</Button>
              <Button className="flex-1" onClick={saveT3} data-testid="t3-save">Use in report</Button>
            </div>
            {t3Url && <img src={t3Url} alt="3D terrain preview" className="mt-2 w-full rounded-md border border-border" data-testid="t3-preview-img" />}
            <p className="text-[11px] text-muted-foreground mt-1">Choose the camera angle for the 3D terrain-with-GeoTIFF figure embedded in the PDF/Word report.</p>
          </div>
        )}
        <div className="border-t border-border pt-3">
          <p className="overline mb-2">Map Studio</p>
          <p className="text-xs text-muted-foreground">A-series page composition (A4–A1) with legends, north arrow, scale bar and grid - Coming in Phase 2. Individual index maps are exportable now via GIS Exports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="overline mb-1">GIS Exports (Targets & Package)</p>
      <Button variant="outline" className="w-full justify-start" onClick={() => dl(`/projects/${id}/export/geojson`, `targets_${project.name}.geojson`, "gj")} disabled={busy === "gj"} data-testid="exp-geojson"><MapTrifold size={16} className="mr-2" />Targets GeoJSON</Button>
      <Button variant="outline" className="w-full justify-start" onClick={() => dl(`/projects/${id}/export/kml`, `targets_${project.name}.kml`, "kml")} disabled={busy === "kml"} data-testid="exp-kml"><MapTrifold size={16} className="mr-2" />Targets KML</Button>
      <Button variant="outline" className="w-full justify-start" onClick={() => dl(`/projects/${id}/export/csv`, `targets_${project.name}.csv`, "csv")} disabled={busy === "csv"} data-testid="exp-csv"><Download size={16} className="mr-2" />Targets CSV</Button>
      <Button className="w-full justify-start mt-2" onClick={() => dl(`/projects/${id}/export/zip`, `SYMIN_${project.name}.zip`, "zip")} disabled={busy === "zip"} data-testid="exp-zip"><FileZip size={16} className="mr-2" />Complete Project Package (ZIP)</Button>
      <p className="text-[11px] text-muted-foreground mt-2">
        Package contains boundary, satellite metadata, target polygons (GeoJSON/KML/CSV), PDF report and disclaimer. GeoTIFF / COG raster export - Coming in Phase 2.
      </p>
    </div>
  );
}
